import { Outlet } from '@tanstack/react-router';

import { AccessPendingScreen } from '@/components/AccessPendingScreen';
import { Sidebar } from '@/components/layout/Sidebar';
import { ParticlesBackground } from '@/components/layout/ParticlesBackground';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useCurrentUser } from '@/hooks/useUserRole';
import { isFeatureEnabled } from '@/config/featureFlags';
import { isNoRoleError } from '@/utils/errors';
import { SuiteAppShellGate } from '@/layouts/suite/SuiteAppShell';

export const AppShell = () => {
  // Behind the `suiteShell` flag, render the shared suite shell instead of the
  // legacy sidebar. The legacy path below stays intact as the rollback route.
  if (isFeatureEnabled('suiteShell')) {
    return <SuiteAppShellGate />;
  }

  return <LegacyAppShell />;
};

const LegacyAppShell = () => {
  useUserProfile();
  const { error: userError, isLoading: userLoading, refetch } = useCurrentUser();

  if (userLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (userError && isNoRoleError(userError)) {
    return <AccessPendingScreen />;
  }

  if (userError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Something went wrong</h2>
          <p className="text-slate-600 mb-6">
            We couldn&apos;t load your account information. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="flex h-screen w-full overflow-hidden relative">
        <ParticlesBackground />
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden relative z-[1]">
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};
